import React, { lazy, Suspense, useState } from "react";
import { TabContent, TabGrid } from "../../CourseCreate/TabLayout";

import { useCourseDocuments } from "api/hooks";
import { useDocumentsV2Enabled } from "api/hooks/useDocumentsV2Enabled";
import { PaziaLoader } from "components/atoms";
import { CourseDocumentList } from "../../CourseCreate/Documents/CourseDocumentList";
import { CourseDocumentCreate } from "../../CourseCreate/Documents/CourseDocumentCreate";
import { CourseDocumentEdit } from "../../CourseCreate/Documents/CourseDocumentEdit";
import { courseDocumentsApi } from "api";
import { useToaster } from "utils";

// The 2.0 form pulls in pdfme (several MB), so it loads only when a 2.0 document is opened.
const CourseDocumentV2Form = lazy(() =>
  import("../../CourseCreate/Documents/V2/CourseDocumentV2Form").then((module) => ({
    default: module.CourseDocumentV2Form,
  })),
);

type PageType = "list" | "create" | "edit" | "createV2" | "editV2";

interface EditCourseDocumentsProps {
  courseId: number;
}
export const EditCourseDocuments = ({ courseId }: EditCourseDocumentsProps): JSX.Element | null => {
  const { data, loading, refresh } = useCourseDocuments(courseId);
  const { enabled: documentsV2Enabled } = useDocumentsV2Enabled();
  const { showSuccess, showError } = useToaster();
  const [currentPage, setCurrentPage] = useState<PageType>("list");
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);

  const onDelete = async (courseDocumentId: number) => {
    try {
      await courseDocumentsApi.delete(courseId, courseDocumentId);
      showSuccess("Usunięto dokument");
      refresh();
    } catch (e) {
      showError("Nie udało się usunąć dokumentu");
      console.error(e);
    }
  };

  const handleNavigateToCreate = () => {
    setCurrentPage("create");
  };

  const handleNavigateToCreateV2 = () => {
    setSelectedDocumentId(null);
    setCurrentPage("createV2");
  };

  // A 1.0 document opens in the old form, a 2.0 document in the new one.
  const handleNavigateToEdit = (documentId: number, version: 1 | 2) => {
    setSelectedDocumentId(documentId);
    setCurrentPage(version === 2 ? "editV2" : "edit");
  };

  // A document 2.0 that was just created stays open: its extra pages can only be added once it exists.
  const handleCreatedV2 = (documentId: number) => {
    refresh();
    setSelectedDocumentId(documentId);
    setCurrentPage("editV2");
  };

  const handleNavigateToList = () => {
    setCurrentPage("list");
    setSelectedDocumentId(null);
  };

  if (loading) return <PaziaLoader position="center" />;

  return (
    <TabContent>
      <TabGrid>
        {currentPage === "list" && (
          <CourseDocumentList
            documents={data}
            onDelete={onDelete}
            courseId={courseId}
            onNavigateToCreate={handleNavigateToCreate}
            onNavigateToCreateV2={handleNavigateToCreateV2}
            documentsV2Enabled={documentsV2Enabled}
            onNavigateToEdit={handleNavigateToEdit}
          />
        )}
      </TabGrid>
      <div className="">
        {currentPage === "create" && (
          <CourseDocumentCreate
            courseId={courseId}
            refreshList={() => refresh()}
            onNavigateToList={handleNavigateToList}
          />
        )}
        {currentPage === "edit" && selectedDocumentId && (
          <CourseDocumentEdit courseId={courseId} courseDocumentId={selectedDocumentId} />
        )}
        {(currentPage === "createV2" || currentPage === "editV2") && (
          <Suspense fallback={<PaziaLoader position="center" />}>
            <CourseDocumentV2Form
              // Creating and editing are different documents: a fresh form (and a fresh cache) for each.
              key={currentPage === "editV2" ? `edit-${selectedDocumentId}` : "create"}
              courseId={courseId}
              courseDocumentId={currentPage === "editV2" ? (selectedDocumentId ?? undefined) : undefined}
              onNavigateToList={handleNavigateToList}
              onSaved={() => refresh()}
              onCreated={handleCreatedV2}
            />
          </Suspense>
        )}
      </div>
    </TabContent>
  );
};
